import axios from "axios"
import { API_BASE_URL } from "./CONSTANTS"
import { kc, authEnabled } from "../config/keycloakConfig"
import {
  getLocalStorageItem,
  setLocalStoragewithoutStringify,
  STORAGE_KEYS,
} from "../Helper/localStorageHandler"

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
})

let toastShown = false

/**
 * Request Interceptor
 * - Check internet connectivity
 * - Attach JWT token from Keycloak or localStorage
 */
axiosClient.interceptors.request.use(
  (config) => {
    // Internet check
    if (!navigator?.onLine) {
      if (!toastShown) {
        toastShown = true
        console.error("No internet connection")
        // Reset flag after few seconds
        setTimeout(() => {
          toastShown = false
        }, 5000)
      }

      return Promise.reject(new Error("No internet connection"))
    }

    // Get token from localStorage
    const token =
      (authEnabled && kc?.token) ||
      getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN)

    // Attach Authorization header only if not already present
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error),
)

/**
 * Response Interceptor
 * - Refresh token on 401
 * - Retry failed request once
 */
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true

      try {
        if (authEnabled && kc) {
          // Refresh Keycloak token
          await kc.updateToken(5)

          const newToken = kc.token

          if (newToken) {
            setLocalStoragewithoutStringify(STORAGE_KEYS.ACCESS_TOKEN, newToken)

            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }
        } else {
          // Fallback token
          const storedToken = getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN)

          if (storedToken) {
            originalRequest.headers.Authorization = `Bearer ${storedToken}`
          }
        }

        return axiosClient(originalRequest)
      } catch (refreshError) {
        // kc.onAuthRefreshError handles redirecting to login, so we don't
        // duplicate that here.
        console.error("Token refresh failed:", refreshError)

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

export default axiosClient
